import React, { useState } from 'react'
import { connect } from 'react-redux'
import { Route, withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'

import AberdeenSymbol from '../../components/AberdeenSymbol'

import {
  requestSignOut,
  requestNavigation
} from '../../stores/auth'

import {
  UPLOADING,
  COMPLETE,
  FAILED,
  QUEUED
} from '../../stores/upload'

import './index.css'

function AuthRoute({
  component: C,
  accountName,
  contactName,
  contactEmail,
  aberdeenImpersonator,
  primaryPostOpsAccountManagerName,
  primaryPostOpsAccountManagerEmail,
  primaryPostOpsAccountManagerPhone,
  primaryLiveOpsAccountManagerName,
  primaryLiveOpsAccountManagerEmail,
  primaryLiveOpsAccountManagerPhone,
  primaryAccountSalesEngineerName,
  primaryAccountSalesEngineerEmail,
  primaryAccountSalesEngineerPhone,
  showInvoicesTab,
  uploads,
  requestSignOut,
  requestNavigation,
  ...rest
}) {
  const [contactOpen, setContactOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)

  let pending = 0, complete = 0, failed = 0

  for (const [, value] of Object.entries(uploads)) {
    if (value.status === UPLOADING || value.status === QUEUED) pending++
    if (value.status === COMPLETE) complete++
    if (value.status === FAILED) failed++
  }

  const initials = contactName
    ? contactName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '?'

  const navItems = [
    { label: 'Upload', path: '/upload' },
    { label: 'Orders', path: '/orders' },
    ...(showInvoicesTab ? [{ label: 'Invoices', path: '/invoices' }] : []),
  ]

  const uploadCount = pending + complete + failed
  const countColor =
    failed > 0 ? 'bg-destructive' : pending > 0 ? 'bg-primary' : 'bg-success'

  return (
    <Route
      {...rest}
      render={props => (
        <div className="flex min-h-full flex-col">
          {/* ── Header ── */}
          <header className="sticky top-0 z-30 border-b bg-card">
            <div className="flex h-14 items-center gap-1 px-3 sm:px-4">
              {/* Logo */}
              <a
                href="https://aberdeen.io"
                target="_blank"
                rel="noopener noreferrer"
                className="mr-3 flex shrink-0 items-center gap-2 text-inherit no-underline"
              >
                <AberdeenSymbol className="inline-flex h-11 w-[47px] shrink-0 items-center" />
                <span className="text-base font-semibold tracking-tight">Aberdeen Hub</span>
              </a>

              {/* Nav links */}
              <nav className="hidden flex-1 items-center gap-1 sm:flex">
                {navItems.map(item => {
                  const isActive = props.location.pathname.startsWith(item.path)
                  const showCount = item.path === '/upload' && uploadCount > 0
                  return (
                    <button
                      key={item.path}
                      onClick={() => requestNavigation(item.path)}
                      className={cn(
                        'relative inline-flex h-14 items-center px-3 text-sm transition-colors',
                        isActive
                          ? 'font-semibold text-primary'
                          : 'text-muted-foreground hover:text-foreground',
                        isActive &&
                          'after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-t after:bg-primary after:content-[""]'
                      )}
                    >
                      {item.label}
                      {showCount && (
                        <span
                          className={cn(
                            'ml-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-bold leading-none text-white',
                            countColor
                          )}
                        >
                          {uploadCount}
                        </span>
                      )}
                    </button>
                  )
                })}
              </nav>

              {/* Contact Us */}
              <button
                onClick={() => setContactOpen(true)}
                className="hidden h-14 items-center px-3 text-sm text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
              >
                Contact Us
              </button>

              {/* User */}
              <div className="ml-auto flex items-center gap-2 sm:ml-1">
                {aberdeenImpersonator && (
                  <span className="text-xs font-bold text-warning">VIEWING AS</span>
                )}
                <span className="hidden text-sm text-muted-foreground md:block">{contactName}</span>
                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <button className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                          <Avatar>
                            <AvatarFallback>{initials}</AvatarFallback>
                          </Avatar>
                        </button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    {accountName && <TooltipContent>{accountName}</TooltipContent>}
                  </Tooltip>
                  <DropdownMenuContent align="end" className="w-60">
                    <div className="px-2 py-1.5">
                      <div className="truncate text-sm font-semibold">{accountName}</div>
                      <div className="truncate text-xs text-muted-foreground">{contactEmail}</div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => setReportOpen(true)}>
                      Report an Issue
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => requestSignOut()}
                      className="text-destructive focus:text-destructive"
                    >
                      Log Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Contact Us dialog */}
          <Dialog open={contactOpen} onOpenChange={setContactOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{accountName} — Your Dedicated Team</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-3 text-sm">
                {primaryAccountSalesEngineerName && (
                  <div>
                    <div className="text-xs text-muted-foreground">Sales Engineer</div>
                    <div className="font-medium">{primaryAccountSalesEngineerName}</div>
                    {primaryAccountSalesEngineerEmail && (
                      <a className="text-primary hover:underline" href={`mailto:${primaryAccountSalesEngineerEmail}`}>{primaryAccountSalesEngineerEmail}</a>
                    )}
                    {primaryAccountSalesEngineerPhone && (
                      <a className="block text-primary hover:underline" href={`tel:${primaryAccountSalesEngineerPhone}`}>{primaryAccountSalesEngineerPhone}</a>
                    )}
                  </div>
                )}
                {primaryPostOpsAccountManagerName && (
                  <div>
                    <div className="text-xs text-muted-foreground">Post Production Account Manager</div>
                    <div className="font-medium">{primaryPostOpsAccountManagerName}</div>
                    {primaryPostOpsAccountManagerEmail && (
                      <a className="text-primary hover:underline" href={`mailto:${primaryPostOpsAccountManagerEmail}`}>{primaryPostOpsAccountManagerEmail}</a>
                    )}
                    {primaryPostOpsAccountManagerPhone && (
                      <a className="block text-primary hover:underline" href={`tel:${primaryPostOpsAccountManagerPhone}`}>{primaryPostOpsAccountManagerPhone}</a>
                    )}
                  </div>
                )}
                {primaryLiveOpsAccountManagerName && (
                  <div>
                    <div className="text-xs text-muted-foreground">Live Monitored Mailbox</div>
                    <a className="text-primary hover:underline" href="mailto:caption@aberdeen.io">caption@aberdeen.io</a>
                  </div>
                )}
                <Separator />
                <div>
                  <div className="text-xs text-muted-foreground">Accounting Department</div>
                  <div className="text-sm">
                    <a className="text-primary hover:underline" href="mailto:accounting@aberdeen.io">accounting@aberdeen.io</a>
                    <span className="text-muted-foreground"> · </span>
                    <a className="text-primary hover:underline" href="tel:9498584463">(949) 858-4463</a>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">AberFast 24-hour Emergency</div>
                  <a className="text-primary hover:underline" href="tel:9492161063">(949) 216-1063</a>
                </div>
                {primaryLiveOpsAccountManagerName && (
                  <div>
                    <div className="text-xs text-muted-foreground">Live Captioning 24-hour Emergency</div>
                    <a className="text-primary hover:underline" href="tel:9493020004">(949) 302-0004</a>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setContactOpen(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Report an Issue dialog */}
          <Dialog open={reportOpen} onOpenChange={setReportOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Report an Issue</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <p>
                  For immediate assistance, call our 24-hour emergency line:{' '}
                  <a className="text-primary hover:underline" href="tel:9492161063">(949) 216-1063</a>.
                </p>
                <p>
                  If your issue is not an emergency, email{' '}
                  <a className="text-primary hover:underline" href="mailto:postadmin@aberdeen.io">postadmin@aberdeen.io</a>.
                  We'll respond during normal business hours.
                </p>
                <p>
                  For general feedback on Aberdeen Hub, email{' '}
                  <a className="text-primary hover:underline" href="mailto:admin@aberdeen.io">admin@aberdeen.io</a>.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReportOpen(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Page content */}
          <main className="flex-1 px-3 py-3 sm:px-4 sm:py-4">
            <C {...props} />
          </main>

          <footer className="border-t py-3 text-center">
            <a
              href="https://aberdeen.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:underline"
            >
              {new Date().getFullYear()} © Aberdeen Hub
            </a>
          </footer>
        </div>
      )}
    />
  )
}

const mapStateToProps = (state) => {
  const {
    accountName,
    contactName,
    contactEmail,
    aberdeenImpersonator,
    primaryPostOpsAccountManagerName,
    primaryPostOpsAccountManagerEmail,
    primaryPostOpsAccountManagerPhone,
    primaryLiveOpsAccountManagerName,
    primaryLiveOpsAccountManagerEmail,
    primaryLiveOpsAccountManagerPhone,
    primaryAccountSalesEngineerName,
    primaryAccountSalesEngineerEmail,
    primaryAccountSalesEngineerPhone,
    showInvoicesTab
  } = state.stores.auth

  const { uploads } = state.stores.upload

  return {
    uploads,
    accountName,
    contactName,
    contactEmail,
    aberdeenImpersonator,
    primaryPostOpsAccountManagerName,
    primaryPostOpsAccountManagerEmail,
    primaryPostOpsAccountManagerPhone,
    primaryLiveOpsAccountManagerName,
    primaryLiveOpsAccountManagerEmail,
    primaryLiveOpsAccountManagerPhone,
    primaryAccountSalesEngineerName,
    primaryAccountSalesEngineerEmail,
    primaryAccountSalesEngineerPhone,
    showInvoicesTab
  }
}

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators({ requestSignOut, requestNavigation }, dispatch)
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AuthRoute))
